import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft, FaSearch, FaCheckCircle, FaTimesCircle, FaClock, FaSync, FaFilter, FaUser, FaCalendarAlt, FaPhone, FaEnvelope, FaFileExcel, FaFilePdf } from "react-icons/fa";
import { getAttendanceReports, exportAttendanceReport } from "../../services/schoolService";
import "../teacher/TeacherLayer.css";

const SingleClassAttendanceLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [fastFilter, setFastFilter] = useState("today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [gender, setGender] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [className, setClassName] = useState(`Class ID: ${id}`);
  const [loading, setLoading] = useState(true);

  // Student history modal states
  const [historyStudent, setHistoryStudent] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalProfile, setModalProfile] = useState(null);
  const [modalSummary, setModalSummary] = useState(null);
  const [modalHistory, setModalHistory] = useState([]);
  const [modalPeriod, setModalPeriod] = useState("this_month");
  const [modalFromDate, setModalFromDate] = useState("");
  const [modalToDate, setModalToDate] = useState("");
  
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    const year = parts[0];
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    if (month >= 0 && month < 12) {
      return `${day} ${months[month]} ${year}`;
    }
    return dateStr;
  };
  // Debounced search term
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { action: "class_view", class_id: id };
      if (debouncedSearch) {
        params.search = debouncedSearch;
        params.student_search = debouncedSearch;
      }
      if (filterStatus) params.status = filterStatus;
      if (gender) params.gender = gender;
      
      if (fastFilter !== "custom") {
        params.date_period = fastFilter;
      } else {
        params.date_period = "custom";
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
      }

      const res = await getAttendanceReports(params);
      if (res?.status && res?.classes_data?.length > 0) {
        const classData = res.classes_data[0];
        setClassName(classData.class_name || `Class ID: ${id}`);
        setSummary(classData.summary_stats || classData.summary_counters || null);
        setStudents(classData.students || classData.students_history || []);
      } else {
        setStudents([]);
        setSummary(null);
      }
    } catch (error) {
      console.error(error);
      setStudents([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [id, debouncedSearch, filterStatus, fastFilter, fromDate, toDate, gender]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchHistory = useCallback(async () => {
    if (!historyStudent) return;
    setModalLoading(true);
    try {
      const params = {
        action: "student_history",
        student_id: historyStudent.student_id || historyStudent.id
      };
      if (modalPeriod !== "custom") {
        params.date_period = modalPeriod;
      } else {
        params.date_period = "custom";
        if (modalFromDate) params.from_date = modalFromDate;
        if (modalToDate) params.to_date = modalToDate;
      }
      const res = await getAttendanceReports(params);
      if (res?.status) {
        setModalProfile(res.student_profile || null);
        setModalSummary(res.summary_stats || null);
        setModalHistory(res.history || []);
      } else {
        setModalProfile(null);
        setModalSummary(null);
        setModalHistory([]);
      }
    } catch (err) {
      console.error(err);
      setModalProfile(null);
      setModalSummary(null);
      setModalHistory([]);
    } finally {
      setModalLoading(false);
    }
  }, [historyStudent, modalPeriod, modalFromDate, modalToDate]);

  useEffect(() => {
    if (historyStudent) {
      fetchHistory();
    }
  }, [historyStudent, fetchHistory]);

  const filteredStudents = students.filter(student => {
    let match = true;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      if (!student.name?.toLowerCase().includes(searchLower) && !student.roll_number?.toLowerCase().includes(searchLower)) {
        match = false;
      }
    }
    if (filterStatus) {
      const fsLower = filterStatus.toLowerCase();
      if (student.history && student.history.length > 0) {
        const hasMatchInHistory = student.history.some(h => {
          const stat = h.attendance_status || h.status || "";
          return stat.toLowerCase() === fsLower;
        });
        const mainStat = student.attendance_status || "";
        if (!hasMatchInHistory && mainStat.toLowerCase() !== fsLower) {
          match = false;
        }
      } else {
        const mainStat = student.attendance_status || "";
        if (mainStat.toLowerCase() !== fsLower) {
          match = false;
        }
      }
    }
    if (gender && student.gender?.toLowerCase() !== gender.toLowerCase()) {
      match = false;
    }
    return match;
  });

  const totalPages = Math.ceil(filteredStudents.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstRecord, indexOfLastRecord);

  const isDateRange = fastFilter !== "today" && students.length > 0 && students[0]?.history && students[0].history.length > 1;
  const matrixDates = isDateRange ? students[0].history.map(h => h.date) : [];

  const getStudentSummaryStats = (student) => {
    let p = 0, a = 0, l = 0, hd = 0, lv = 0, nm = 0;
    if (student.history && student.history.length > 0) {
      student.history.forEach(h => {
        const stat = (h.attendance_status || h.status || "").trim().toLowerCase();
        if (stat === 'present') p++;
        else if (stat === 'absent') a++;
        else if (stat === 'late') l++;
        else if (stat === 'half day' || stat === 'halfday' || stat === 'half_day') hd++;
        else if (stat === 'leave') lv++;
        else nm++;
      });
    } else {
      const stat = (student.attendance_status || student.status || "").trim().toLowerCase();
      if (stat === 'present') p++;
      else if (stat === 'absent') a++;
      else if (stat === 'late') l++;
      else if (stat === 'half day' || stat === 'halfday' || stat === 'half_day') hd++;
      else if (stat === 'leave') lv++;
      else nm++;
    }
    return { p, a, l, hd, lv, nm };
  };

  const getDateColumnTotals = (dateIndex) => {
    let count = 0;
    filteredStudents.forEach(st => {
      const h = st.history && st.history[dateIndex];
      if (h) {
        const stat = (h.attendance_status || h.status || "").trim().toLowerCase();
        if (stat === 'present' || stat === 'late' || stat === 'half day' || stat === 'halfday') {
          count++;
        }
      }
    });
    return count;
  };

  const overallSummaryTotals = filteredStudents.reduce((acc, st) => {
    const stats = getStudentSummaryStats(st);
    acc.p += stats.p;
    acc.a += stats.a;
    acc.l += stats.l;
    acc.hd += stats.hd;
    acc.lv += stats.lv;
    acc.nm += stats.nm;
    return acc;
  }, { p: 0, a: 0, l: 0, hd: 0, lv: 0, nm: 0 });

  const getRegisterBannerTitle = (period) => {
    if (period === "today") return "DAILY ATTENDANCE REGISTER";
    if (period === "7_days") return "WEEKLY ATTENDANCE REGISTER";
    if (period === "this_month") return "MONTHLY ATTENDANCE REGISTER";
    return "ATTENDANCE REGISTER";
  };

  const handleExport = async (format, params, fileName) => {
    try {
      toast.info(`Generating ${format.toUpperCase()} report...`);
      const res = await exportAttendanceReport(params);
      if (res?.download_url) {
        window.open(res.download_url, "_blank");
        toast.success(`${format.toUpperCase()} report ready!`);
      } else {
        toast.error(res?.message || `Failed to generate ${format.toUpperCase()} report.`);
      }
    } catch (err) {
      console.error(err);
      toast.error(`Failed to export ${format.toUpperCase()} report.`);
    }
  };

  const getExportParams = (format) => {
    const params = { export_format: format, class_id: id, date_period: fastFilter };
    if (fastFilter === "custom") {
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
    }
    return params;
  };

  const handleExportPDF = () => handleExport("pdf", getExportParams("pdf"), `Class_${className || id}_Attendance.pdf`);
  const handleExportExcel = () => handleExport("excel", getExportParams("excel"), `Class_${className || id}_Attendance.xls`);

  const getStudentExportParams = (format) => {
    const studentId = modalProfile?.id || historyStudent?.id || historyStudent?.student_id;
    const params = { export_format: format, class_id: id, date_period: modalPeriod };
    if (studentId) params.student_id = studentId;
    if (modalPeriod === "custom") {
      if (modalFromDate) params.from_date = modalFromDate;
      if (modalToDate) params.to_date = modalToDate;
    }
    return params;
  };

  const studentName = () => (modalProfile?.name || historyStudent?.name || "Student").replace(/\s+/g, "_");

  const handleExportStudentPDF = () => handleExport("pdf", getStudentExportParams("pdf"), `${studentName()}_Attendance.pdf`);
  const handleExportStudentExcel = () => handleExport("excel", getStudentExportParams("excel"), `${studentName()}_Attendance.xls`);

  return (
    <div className="school-wrapper">
      <div className="school-card smooth-card">
        <div className="admin-page-shell">
          {historyStudent ? (
            <div className="student-history-detail-view animate__animated animate__fadeIn">
              <div className="card border-0 mb-4 overflow-hidden" style={{ borderRadius: "16px", boxShadow: "0 10px 30px rgba(0,0,0,0.05)" }}>
                <div style={{ background: "linear-gradient(135deg, #0284c7, #06b6d4)", padding: "30px 24px" }}>
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-4">
                    <div className="d-flex align-items-center gap-3 gap-md-4 w-100">
                      <div 
                        className="d-flex align-items-center justify-content-center text-primary fw-bold shadow-sm bg-white"
                        style={{ 
                          width: "70px", 
                          height: "70px", 
                          fontSize: "30px",
                          borderRadius: "20px",
                          flexShrink: 0
                        }}
                      >
                        {(modalProfile?.name || historyStudent.name || "?").charAt(0).toUpperCase()}
                      </div>
                      
                      <div>
                        <h3 className="fw-bold text-white mb-2 fs-4 fs-md-3">
                          {modalProfile?.name || historyStudent.name}
                        </h3>
                        <div className="d-flex flex-wrap gap-2">
                          <span className="badge bg-white bg-opacity-25 text-white px-3 py-1.5 rounded-pill fw-medium" style={{ fontSize: "12px", backdropFilter: "blur(4px)" }}>
                            Roll: {modalProfile?.roll_number || historyStudent.roll_number || "N/A"}
                          </span>
                          <span className="badge bg-white bg-opacity-25 text-white px-3 py-1.5 rounded-pill fw-medium" style={{ fontSize: "12px", backdropFilter: "blur(4px)" }}>
                            Class: {className}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-2 w-100 justify-content-md-end">
                       <button 
                         onClick={handleExportStudentExcel} 
                         disabled={modalLoading || modalHistory.length === 0}
                         className="btn btn-success text-white d-flex align-items-center gap-2 rounded-pill px-3 py-2 shadow-sm"
                         title="Download Excel"
                       >
                          <FaFileExcel /> <span className="d-none d-sm-inline fw-semibold" style={{ fontSize: "14px" }}>Excel</span>
                       </button>
                       <button 
                         onClick={handleExportStudentPDF} 
                         disabled={modalLoading || modalHistory.length === 0}
                         className="btn btn-danger text-white d-flex align-items-center gap-2 rounded-pill px-3 py-2 shadow-sm"
                         title="Download PDF"
                       >
                          <FaFilePdf /> <span className="d-none d-sm-inline fw-semibold" style={{ fontSize: "14px" }}>PDF</span>
                       </button>
                       <button onClick={() => setHistoryStudent(null)} className="btn btn-light bg-white bg-opacity-25 border-0 text-white d-flex align-items-center gap-2 rounded-pill px-3 py-2 shadow-sm" style={{ backdropFilter: "blur(4px)" }}>
                          <FaArrowLeft /> <span className="d-none d-sm-inline fw-bold">Back</span>
                       </button>
                       <button onClick={fetchHistory} className="btn btn-light bg-white text-primary border-0 d-flex align-items-center justify-content-center rounded-circle p-2 shadow-sm" style={{ width: "40px", height: "40px" }} title="Refresh">
                          <FaSync />
                       </button>
                    </div>
                  </div>
                </div>

                <div className="px-4 pb-4">
                  <div className="d-flex flex-wrap gap-4 mt-0 bg-light rounded-4 p-3 border border-light shadow-sm">
                    <div className="d-flex align-items-center gap-3">
                      <div className="bg-white p-2 rounded-circle shadow-sm text-primary d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                        <FaUser size={16} />
                      </div>
                      <div>
                        <div className="text-muted fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.5px", textTransform: "uppercase" }}>Gender</div>
                        <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{modalProfile?.gender || historyStudent.gender || "N/A"}</div>
                      </div>
                    </div>

                    {(modalProfile?.mobile || historyStudent.mobile) && (
                      <div className="d-flex align-items-center gap-3 ms-md-2">
                        <div className="bg-white p-2 rounded-circle shadow-sm text-success d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                          <FaPhone size={14} style={{ transform: "scaleX(-1)" }} />
                        </div>
                        <div>
                          <div className="text-muted fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.5px", textTransform: "uppercase" }}>Mobile</div>
                          <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{modalProfile?.mobile || historyStudent.mobile}</div>
                        </div>
                      </div>
                    )}

                    {(modalProfile?.email || historyStudent.email) && (
                      <div className="d-flex align-items-center gap-3 ms-md-2">
                        <div className="bg-white p-2 rounded-circle shadow-sm text-info d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                          <FaEnvelope size={16} />
                        </div>
                        <div>
                          <div className="text-muted fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.5px", textTransform: "uppercase" }}>Email</div>
                          <div className="fw-bold text-dark text-truncate" style={{ fontSize: "14px", maxWidth: "200px" }}>{modalProfile?.email || historyStudent.email}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="d-flex flex-wrap mb-4" style={{ gap: "12px", width: "100%" }}>
                <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                  <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#ECFDF5", borderColor: "#A7F3D0", borderRadius: "12px" }}>
                    <div className="small fw-semibold text-success mb-1" style={{ fontSize: '13px' }}>Present</div>
                    <div className="fs-3 fw-bold text-success">{modalSummary?.Present || modalSummary?.present || 0}</div>
                  </div>
                </div>
                <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                  <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#FEF2F2", borderColor: "#FECACA", borderRadius: "12px" }}>
                    <div className="small fw-semibold text-danger mb-1" style={{ fontSize: '13px' }}>Absent</div>
                    <div className="fs-3 fw-bold text-danger">{modalSummary?.Absent || modalSummary?.absent || 0}</div>
                  </div>
                </div>
                <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                  <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#FEFCE8", borderColor: "#FEF08A", borderRadius: "12px" }}>
                    <div className="small fw-semibold mb-1" style={{ fontSize: '13px', color: "#b45309" }}>Late</div>
                    <div className="fs-3 fw-bold" style={{ color: "#b45309" }}>{modalSummary?.Late || modalSummary?.late || 0}</div>
                  </div>
                </div>
                <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                  <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#EFF6FF", borderColor: "#BFDBFE", borderRadius: "12px" }}>
                    <div className="small fw-semibold text-primary mb-1" style={{ fontSize: '13px' }}>Half Day</div>
                    <div className="fs-3 fw-bold text-primary">{modalSummary?.["Half Day"] || modalSummary?.half_day || modalSummary?.halfday || 0}</div>
                  </div>
                </div>
                <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                  <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#FAF5FF", borderColor: "#E9D5FF", borderRadius: "12px" }}>
                    <div className="small fw-semibold mb-1" style={{ fontSize: '13px', color: "#7e22ce" }}>Leave</div>
                    <div className="fs-3 fw-bold" style={{ color: "#7e22ce" }}>{modalSummary?.Leave || modalSummary?.leave || 0}</div>
                  </div>
                </div>
                <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                  <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#F8FAFC", borderColor: "#E2E8F0", borderRadius: "12px" }}>
                    <div className="small fw-semibold text-secondary mb-1" style={{ fontSize: '13px' }}>Not Marked</div>
                    <div className="fs-3 fw-bold text-secondary">{modalSummary?.["Not Marked"] || modalSummary?.not_marked || modalSummary?.notmarked || 0}</div>
                  </div>
                </div>
              </div>

              <div className="bg-light p-3 rounded-3 mb-4 border" style={{ borderColor: '#e9ecef' }}>
                <h6 className="mb-3 d-flex align-items-center gap-2 text-secondary fw-bold" style={{ fontSize: '14px' }}>
                  <FaFilter /> Date Period Filters
                </h6>
                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-4">
                    <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}><FaCalendarAlt className="me-1" /> Date Period</label>
                    <select 
                      className="form-select text-center"
                      value={modalPeriod}
                      onChange={(e) => setModalPeriod(e.target.value)}
                      style={{ height: '42px', borderRadius: '8px', border: "1px solid #ced4da", backgroundColor: "#fff" }}
                    >
                      <option value="this_month">This Month</option>
                      <option value="7_days">Last 7 Days</option>
                      <option value="today">Today</option>
                      <option value="custom">Custom Date</option>
                    </select>
                  </div>
                  
                  <div className="col-12 col-sm-6 col-md-4">
                    <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>From Date</label>
                    <input
                      type="date"
                      value={modalFromDate}
                      onChange={(e) => {
                        setModalFromDate(e.target.value);
                        setModalPeriod("custom");
                      }}
                      className="form-control w-100 text-center"
                      style={{ height: '42px', borderRadius: '8px', border: "1px solid #ced4da", backgroundColor: "#fff" }}
                    />
                  </div>

                  <div className="col-12 col-sm-6 col-md-4">
                    <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>To Date</label>
                    <input
                      type="date"
                      value={modalToDate}
                      onChange={(e) => {
                        setModalToDate(e.target.value);
                        setModalPeriod("custom");
                      }}
                      className="form-control w-100 text-center"
                      style={{ height: '42px', borderRadius: '8px', border: "1px solid #ced4da", backgroundColor: "#fff" }}
                    />
                  </div>
                </div>
              </div>
              <div className="p-3 mb-3 rounded-3 text-dark d-flex flex-wrap align-items-center justify-content-between gap-2 border" style={{ background: "linear-gradient(135deg, #e8f7f2 0%, #d8f3e5 100%)", borderColor: "#74c69d" }}>
                <div>
                  <h6 className="fw-bold mb-1 d-flex align-items-center gap-2 text-uppercase" style={{ letterSpacing: "0.5px", fontSize: "15px", color: "#1b4332" }}>
                    <div style={{ width: "6px", height: "18px", backgroundColor: "#2d6a4f", borderRadius: "3px" }}></div>
                    {getRegisterBannerTitle(modalPeriod)} - STUDENT HISTORY
                  </h6>
                  <span className="small fw-medium" style={{ color: "#2d6a4f" }}>
                    Student: <strong>{modalProfile?.name || historyStudent.name}</strong> | Roll: <strong>{modalProfile?.roll_number || historyStudent.roll_number || "N/A"}</strong> | Period: <strong>{modalPeriod === "today" ? "Today" : modalPeriod === "7_days" ? "Last 7 Days" : modalPeriod === "this_month" ? "This Month" : `${modalFromDate || 'Start'} to ${modalToDate || 'End'}`}</strong>
                  </span>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center small bg-white px-3 py-1.5 rounded-pill shadow-sm border" style={{ fontSize: "12px", borderColor: "#95d5b2" }}>
                  <span className="fw-bold me-1" style={{ color: "#1b4332" }}>*Key:</span>
                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill">P = Present</span>
                  <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 rounded-pill">A = Absent</span>
                  <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 rounded-pill">L = Late</span>
                  <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill">HD = Half Day</span>
                  <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill">LV = Leave</span>
                </div>
              </div>

              <div 
                className="teacher-table-shell admin-table-card admin-table-scroll admin-responsive-table border rounded-3 p-2 shadow-sm mb-4 mt-2"
                style={{ backgroundColor: "#e8f7f2", borderColor: "#74c69d", borderWidth: "2px" }}
              >
                {modalLoading ? (
                  <div className="text-center text-muted py-5">
                    <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                    Loading attendance history...
                  </div>
                ) : modalHistory.length === 0 ? (
                  <div className="text-center text-muted py-5 fw-medium">
                    No history records found for the selected date range.
                  </div>
                ) : (
                  <table className="user-table align-middle text-nowrap" style={{ backgroundColor: "#f2fbf7", borderColor: "#74c69d", whiteSpace: "nowrap" }}>
                    <thead>
                      <tr className="text-center align-middle" style={{ backgroundColor: "#b7e4c7", color: "#1b4332" }}>
                        <th style={{ backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11.5px" }}>Date</th>
                        <th style={{ backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11.5px" }}>Status</th>
                        <th style={{ backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11.5px" }}>Check In</th>
                        <th style={{ backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11.5px" }}>Check Out</th>
                        <th style={{ backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11.5px" }}>Punch Type</th>
                        <th style={{ backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11.5px" }}>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalHistory.map((item, idx) => {
                        const itemStatus = (item.status || item.attendance_status || "Not Marked").trim();
                        const stLower = itemStatus.toLowerCase();
                        const punchType = item.type || item.attendance_type;
                        const reason = item.reason || item.absent_reason;

                        let statusTd = <td className="text-center text-muted" style={{ backgroundColor: "#f2fbf7", border: "1px solid #74c69d", fontSize: "11px", whiteSpace: "nowrap" }}>-</td>;

                        if (stLower === 'present') {
                          statusTd = <td className="text-center fw-bold" style={{ backgroundColor: "#d8f3dc", color: "#1b4332", border: "1px solid #74c69d", fontSize: "11px", whiteSpace: "nowrap" }}>P (Present)</td>;
                        } else if (stLower === 'absent') {
                          statusTd = <td className="text-center fw-bold" style={{ backgroundColor: "#ffccd5", color: "#800f2f", border: "1px solid #74c69d", fontSize: "11px", whiteSpace: "nowrap" }}>A (Absent)</td>;
                        } else if (stLower === 'late') {
                          statusTd = <td className="text-center fw-bold" style={{ backgroundColor: "#fff3b0", color: "#9a031e", border: "1px solid #74c69d", fontSize: "11px", whiteSpace: "nowrap" }}>L (Late)</td>;
                        } else if (stLower === 'half day' || stLower === 'halfday' || stLower === 'half_day') {
                          statusTd = <td className="text-center fw-bold" style={{ backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #74c69d", fontSize: "11px", whiteSpace: "nowrap" }}>HD (Half Day)</td>;
                        } else if (stLower === 'leave') {
                          statusTd = <td className="text-center fw-bold" style={{ backgroundColor: "#f3e8ff", color: "#6b21a8", border: "1px solid #74c69d", fontSize: "11px", whiteSpace: "nowrap" }}>LV (Leave)</td>;
                        }

                        return (
                          <tr key={idx} className="smooth-table-row text-center align-middle">
                            <td className="fw-bold text-dark text-nowrap" style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7", whiteSpace: "nowrap", fontSize: "11.5px" }}>
                              {formatDateDisplay(item.date)}
                            </td>
                            {statusTd}
                            <td className="text-secondary fw-semibold text-nowrap" style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7", whiteSpace: "nowrap", fontSize: "11.5px" }}>
                              {item.clock_in || "-"}
                            </td>
                            <td className="text-secondary fw-semibold text-nowrap" style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7", whiteSpace: "nowrap", fontSize: "11.5px" }}>
                              {item.clock_out || "-"}
                            </td>
                            <td style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7", whiteSpace: "nowrap", fontSize: "11px" }}>
                              <span className="badge bg-light text-secondary border px-2 py-0.5" style={{ fontSize: "11px" }}>
                                {punchType || "N/A"}
                              </span>
                            </td>
                            <td className="text-muted small text-nowrap" style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7", whiteSpace: "nowrap", maxWidth: "200px" }}>
                              {reason || "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="admin-page-header list-header flex-wrap">
                <div className="d-flex align-items-center gap-3">
                  <button 
                    onClick={() => navigate(-1)} 
                    className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-circle"
                    style={{ width: "35px", height: "35px" }}
                  >
                    <FaArrowLeft />
                  </button>
                  <div>
                    <h5 className="admin-page-title mb-0">{className} Attendance</h5>
                    <p className="text-muted mt-1 mb-0" style={{ fontSize: "14px" }}>
                      Detailed View: In & Out Timings
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button 
                    className="btn btn-success text-white d-inline-flex align-items-center gap-2 rounded-pill px-3 py-2 shadow-sm"
                    onClick={handleExportExcel}
                    disabled={loading || filteredStudents.length === 0}
                    title="Download Excel Register"
                  >
                    <FaFileExcel /> <span className="d-none d-sm-inline fw-semibold" style={{ fontSize: "14px" }}>Excel</span>
                  </button>
                  <button 
                    className="btn btn-danger text-white d-inline-flex align-items-center gap-2 rounded-pill px-3 py-2 shadow-sm"
                    onClick={handleExportPDF}
                    disabled={loading || filteredStudents.length === 0}
                    title="Download PDF Register"
                  >
                    <FaFilePdf /> <span className="d-none d-sm-inline fw-semibold" style={{ fontSize: "14px" }}>PDF</span>
                  </button>
                  <button className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-circle shadow-sm" onClick={fetchData} style={{ width: '40px', height: '40px' }} title="Refresh">
                    <FaSync className="text-secondary" />
                  </button>
                </div>
              </div>

              <div className="bg-light p-4 rounded-3 mb-4 mt-4 border student-filter-card" style={{ borderColor: '#e9ecef' }}>
                <h6 className="mb-3 d-flex align-items-center gap-2 text-secondary">
                  <FaFilter /> Advanced Filters
                </h6>
                
                <div className="row g-3 align-items-end mb-3">
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Search Student</label>
                    <div className="position-relative">
                      <FaSearch className="position-absolute text-muted" style={{ top: "14px", left: "14px" }} />
                      <input 
                        type="text" 
                        placeholder="Search Name / Roll No..." 
                        className="form-control form-control-lg bg-light border-0 shadow-none ps-5"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Date Period</label>
                    <select 
                      className="form-select form-select-lg bg-light border-0 shadow-none"
                      style={{ borderRadius: '10px', fontSize: '15px' }}
                      value={fastFilter}
                      onChange={(e) => {
                        setFastFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="today">Today</option>
                      <option value="7_days">Last 7 Days</option>
                      <option value="this_month">This Month</option>
                      <option value="custom">Custom Date</option>
                    </select>
                  </div>
                  
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>From Date</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        setFastFilter("custom");
                        setCurrentPage(1);
                      }}
                      className="form-control form-control-lg bg-light border-0 shadow-none text-center"
                      style={{ borderRadius: '10px', fontSize: '15px' }}
                    />
                  </div>

                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>To Date</label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                        setFastFilter("custom");
                        setCurrentPage(1);
                      }}
                      className="form-control form-control-lg bg-light border-0 shadow-none text-center"
                      style={{ borderRadius: '10px', fontSize: '15px' }}
                    />
                  </div>
                </div>

                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-4 col-lg-4">
                    <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Status</label>
                    <select 
                      className="form-select form-select-lg bg-light border-0 shadow-none text-center"
                      style={{ borderRadius: '10px', fontSize: '15px' }}
                      value={filterStatus}
                      onChange={(e) => {
                        setFilterStatus(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All Statuses</option>
                      <option value="Present">Present</option>
                      <option value="Absent">Absent</option>
                      <option value="Late">Late</option>
                      <option value="Half Day">Half Day</option>
                      <option value="Leave">Leave</option>
                      <option value="Not Marked">Not Marked</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-4 col-lg-4">
                    <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Gender</label>
                    <select 
                      className="form-select form-select-lg bg-light border-0 shadow-none text-center"
                      style={{ borderRadius: '10px', fontSize: '15px' }}
                      value={gender}
                      onChange={(e) => {
                        setGender(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All Genders</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-4 col-lg-4">
                    <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Limit</label>
                    <select 
                      className="form-select form-select-lg bg-light border-0 shadow-none text-center"
                      style={{ borderRadius: '10px', fontSize: '15px' }}
                      value={recordsPerPage}
                      onChange={(e) => {
                        setRecordsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10 Entries</option>
                      <option value={25}>25 Entries</option>
                      <option value={50}>50 Entries</option>
                      <option value={100}>100 Entries</option>
                    </select>
                  </div>
                </div>
                <div className="d-flex flex-wrap mt-4 mb-2" style={{ gap: "12px", width: "100%" }}>
                  <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                    <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#F0F9FF", borderColor: "#BAE6FD", borderRadius: "12px" }}>
                      <div className="small fw-semibold text-primary mb-1" style={{ fontSize: '13px' }}>Total Match</div>
                      <div className="fs-3 fw-bold text-primary">{summary?.total_students ?? summary?.total_students_matched ?? 0}</div>
                    </div>
                  </div>
                  <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                    <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#ECFDF5", borderColor: "#A7F3D0", borderRadius: "12px" }}>
                      <div className="small fw-semibold text-success mb-1" style={{ fontSize: '13px' }}>Present</div>
                      <div className="fs-3 fw-bold text-success">{summary?.present ?? 0}</div>
                    </div>
                  </div>
                  <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                    <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#FEF2F2", borderColor: "#FECACA", borderRadius: "12px" }}>
                      <div className="small fw-semibold text-danger mb-1" style={{ fontSize: '13px' }}>Absent</div>
                      <div className="fs-3 fw-bold text-danger">{summary?.absent ?? 0}</div>
                    </div>
                  </div>
                  <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                    <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#FEFCE8", borderColor: "#FEF08A", borderRadius: "12px" }}>
                      <div className="small fw-semibold mb-1" style={{ fontSize: '13px', color: "#b45309" }}>Late</div>
                      <div className="fs-3 fw-bold" style={{ color: "#b45309" }}>{summary?.late ?? 0}</div>
                    </div>
                  </div>
                  <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                    <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#EFF6FF", borderColor: "#BFDBFE", borderRadius: "12px" }}>
                      <div className="small fw-semibold text-primary mb-1" style={{ fontSize: '13px' }}>Half Day</div>
                      <div className="fs-3 fw-bold text-primary">{summary?.half_day ?? 0}</div>
                    </div>
                  </div>
                  <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                    <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#FAF5FF", borderColor: "#E9D5FF", borderRadius: "12px" }}>
                      <div className="small fw-semibold mb-1" style={{ fontSize: '13px', color: "#7e22ce" }}>Leave</div>
                      <div className="fs-3 fw-bold" style={{ color: "#7e22ce" }}>{summary?.leave ?? 0}</div>
                    </div>
                  </div>
                  <div style={{ flex: "1 1 130px", minWidth: "110px" }}>
                    <div className="p-3 border rounded text-center h-100 shadow-sm" style={{ background: "#F8FAFC", borderColor: "#E2E8F0", borderRadius: "12px" }}>
                      <div className="small fw-semibold text-secondary mb-1" style={{ fontSize: '13px' }}>Not Marked</div>
                      <div className="fs-3 fw-bold text-secondary">{summary?.not_marked ?? 0}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-3 mb-3 rounded-3 text-dark d-flex flex-wrap align-items-center justify-content-between gap-2 border" style={{ background: "linear-gradient(135deg, #e8f7f2 0%, #d8f3e5 100%)", borderColor: "#74c69d" }}>
                <div>
                  <h6 className="fw-bold mb-1 d-flex align-items-center gap-2 text-uppercase" style={{ letterSpacing: "0.5px", fontSize: "15px", color: "#1b4332" }}>
                    <div style={{ width: "6px", height: "18px", backgroundColor: "#2d6a4f", borderRadius: "3px" }}></div>
                    {getRegisterBannerTitle(fastFilter)}
                  </h6>
                  <span className="small fw-medium" style={{ color: "#2d6a4f" }}>
                    Class: <strong>{className}</strong> | Period: <strong>{fastFilter === "today" ? "Today" : fastFilter === "7_days" ? "Last 7 Days" : fastFilter === "this_month" ? "This Month" : `${fromDate || 'Start'} to ${toDate || 'End'}`}</strong>
                  </span>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center small bg-white px-3 py-1.5 rounded-pill shadow-sm border" style={{ fontSize: "12px", borderColor: "#95d5b2" }}>
                  <span className="fw-bold me-1" style={{ color: "#1b4332" }}>*Key:</span>
                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill">P = Present</span>
                  <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 rounded-pill">A = Absent</span>
                  <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 rounded-pill">L = Late</span>
                  <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill">HD = Half Day</span>
                  <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill">LV = Leave</span>
                </div>
              </div>

              <div 
                className="teacher-table-shell admin-table-card admin-table-scroll admin-responsive-table border rounded-3 p-2 shadow-sm mb-4"
                style={{ backgroundColor: "#e8f7f2", borderColor: "#74c69d", borderWidth: "2px" }}
              >
                <table className="user-table align-middle text-nowrap" style={{ backgroundColor: "#f2fbf7", borderColor: "#74c69d", whiteSpace: "nowrap" }}>
                  <thead>
                    <tr className="text-center align-middle" style={{ backgroundColor: "#b7e4c7", color: "#1b4332" }}>
                      <th rowSpan="2" style={{ width: "55px", backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11px" }}>Serial No</th>
                      <th rowSpan="2" style={{ minWidth: "75px", backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11px" }}>Roll No</th>
                      <th rowSpan="2" className="text-start" style={{ minWidth: "160px", backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11.5px" }}>Name and Designation</th>
                      {!isDateRange && <th rowSpan="2" style={{ backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11px" }}>Gender</th>}
                      {isDateRange ? (
                        <>
                          <th colSpan={matrixDates.length} className="text-center" style={{ letterSpacing: "1px", backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap" }}>
                            ATTENDANCE AND INITIALS
                          </th>
                          <th colSpan="5" className="text-center" style={{ backgroundColor: "#a7f3d0", color: "#064e3b", border: "1px solid #74c69d", whiteSpace: "nowrap" }}>
                            *Summary
                          </th>
                          <th rowSpan="2" className="text-center" style={{ minWidth: "110px", backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap", fontSize: "11px" }}>
                            Remarks & Initials of Officer-In-Charge
                          </th>
                        </>
                      ) : (
                        <>
                          <th rowSpan="2" style={{ backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap" }}>Status</th>
                          <th rowSpan="2" style={{ backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap" }}>Check-In (In Time)</th>
                          <th rowSpan="2" style={{ backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap" }}>Check-Out (Out Time)</th>
                        </>
                      )}
                    </tr>
                    {isDateRange && (
                      <tr className="text-center align-middle">
                        {matrixDates.map((dateStr, idx) => {
                          const dObj = new Date(dateStr);
                          const dayNum = isNaN(dObj.getDate()) ? (idx + 1) : dObj.getDate();
                          const monthName = isNaN(dObj.getDate()) ? 'Jul' : dObj.toLocaleString('en-US', { month: 'short' });
                          return (
                            <th key={idx} className="text-center" style={{ minWidth: "34px", padding: "4px 2px", backgroundColor: "#b7e4c7", color: "#1b4332", border: "1px solid #74c69d", lineHeight: "1.2", whiteSpace: "nowrap" }}>
                              <div style={{ fontSize: "11px", fontWeight: "bold" }}>{dayNum}</div>
                              <div style={{ fontSize: "8.5px", fontWeight: "normal", color: "#2d6a4f" }}>{monthName}</div>
                            </th>
                          );
                        })}
                        <th className="text-center text-success" title="Total Present" style={{ width: "32px", fontSize: "11px", backgroundColor: "#d8f3dc", color: "#1b4332", border: "1px solid #74c69d", whiteSpace: "nowrap" }}>P</th>
                        <th className="text-center text-danger" title="Total Absent" style={{ width: "32px", fontSize: "11px", backgroundColor: "#ffccd5", color: "#800f2f", border: "1px solid #74c69d", whiteSpace: "nowrap" }}>A</th>
                        <th className="text-center" title="Total Late" style={{ width: "32px", fontSize: "11px", backgroundColor: "#fff3b0", color: "#9a031e", border: "1px solid #74c69d", whiteSpace: "nowrap" }}>L</th>
                        <th className="text-center text-info" title="Total Half Day" style={{ width: "32px", fontSize: "11px", backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #74c69d", whiteSpace: "nowrap" }}>HD</th>
                        <th className="text-center text-secondary" title="Total Leave" style={{ width: "32px", fontSize: "11px", backgroundColor: "#f3e8ff", color: "#6b21a8", border: "1px solid #74c69d", whiteSpace: "nowrap" }}>LV</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={isDateRange ? matrixDates.length + 9 : 7} className="text-center text-muted p-4">Loading students...</td>
                      </tr>
                    ) : currentStudents.length > 0 ? (
                      currentStudents.map((student, index) => {
                        const stats = getStudentSummaryStats(student);
                        const serialNo = index + 1 + (currentPage - 1) * recordsPerPage;
                        return (
                          <tr key={student.student_id || Math.random()} className="smooth-table-row text-center align-middle">
                            <td className="fw-semibold text-muted" style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7", whiteSpace: "nowrap", fontSize: "11px" }}>{serialNo}</td>
                            <td className="fw-bold text-secondary" style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7", whiteSpace: "nowrap", fontSize: "11px", minWidth: "75px" }}>{student.roll_number || "-"}</td>
                            <td className="fw-bold text-dark text-start" style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7", whiteSpace: "nowrap", fontSize: "11.5px" }}>
                              <span 
                                className="text-primary" 
                                style={{ cursor: "pointer", borderBottom: "1px dashed #3056de" }}
                                onClick={() => {
                                  setHistoryStudent(student);
                                  setModalPeriod(fastFilter);
                                  setModalFromDate(fromDate);
                                  setModalToDate(toDate);
                                }}
                                title="Click to view attendance history"
                              >
                                {student.name}
                              </span>
                            </td>
                            {!isDateRange && (
                              <td style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7" }}>
                                <span className={`badge ${student.gender === 'Male' ? 'bg-primary text-primary' : student.gender === 'Female' ? 'bg-info text-info' : 'bg-secondary text-secondary'} bg-opacity-10 border px-2 py-1`}>
                                  {student.gender}
                                </span>
                              </td>
                            )}
                            
                            {isDateRange ? (
                              <>
                                {(student.history || []).map((h, i) => {
                                  const st = (h.attendance_status || h.status || "").trim().toLowerCase();
                                  if (st === 'present') {
                                    return <td key={i} className="text-center fw-bold" style={{ backgroundColor: "#d8f3dc", color: "#1b4332", border: "1px solid #74c69d", fontSize: "11px", padding: "4px 2px" }}>P</td>;
                                  }
                                  if (st === 'absent') {
                                    return <td key={i} className="text-center fw-bold" style={{ backgroundColor: "#ffccd5", color: "#800f2f", border: "1px solid #74c69d", fontSize: "11px", padding: "4px 2px" }}>A</td>;
                                  }
                                  if (st === 'late') {
                                    return <td key={i} className="text-center fw-bold" style={{ backgroundColor: "#fff3b0", color: "#9a031e", border: "1px solid #74c69d", fontSize: "11px", padding: "4px 2px" }}>L</td>;
                                  }
                                  if (st === 'half day' || st === 'halfday' || st === 'half_day') {
                                    return <td key={i} className="text-center fw-bold" style={{ backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #74c69d", fontSize: "11px", padding: "4px 2px" }}>HD</td>;
                                  }
                                  if (st === 'leave') {
                                    return <td key={i} className="text-center fw-bold" style={{ backgroundColor: "#f3e8ff", color: "#6b21a8", border: "1px solid #74c69d", fontSize: "11px", padding: "4px 2px" }}>LV</td>;
                                  }
                                  return <td key={i} className="text-center text-muted" style={{ backgroundColor: "#f2fbf7", border: "1px solid #74c69d", fontSize: "11px", padding: "4px 2px" }}>-</td>;
                                })}
                                <td className="fw-bold text-center" style={{ backgroundColor: "#d8f3dc", color: "#1b4332", border: "1px solid #74c69d" }}>{stats.p}</td>
                                <td className="fw-bold text-center" style={{ backgroundColor: "#ffccd5", color: "#800f2f", border: "1px solid #74c69d" }}>{stats.a}</td>
                                <td className="fw-bold text-center" style={{ backgroundColor: "#fff3b0", color: "#9a031e", border: "1px solid #74c69d" }}>{stats.l}</td>
                                <td className="fw-bold text-center" style={{ backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #74c69d" }}>{stats.hd}</td>
                                <td className="fw-bold text-center" style={{ backgroundColor: "#f3e8ff", color: "#6b21a8", border: "1px solid #74c69d" }}>{stats.lv}</td>
                                <td className="text-muted small" style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7" }}>-</td>
                              </>
                            ) : (
                              <>
                                <td style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7" }}>
                                  {(() => {
                                    const stat = (student.attendance_status || student.status || "Not Marked").trim();
                                    const statLower = stat.toLowerCase();
                                    if (statLower === 'present') {
                                      return (
                                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1">
                                          <FaCheckCircle /> Present
                                        </span>
                                      );
                                    }
                                    if (statLower === 'absent') {
                                      return (
                                        <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1">
                                          <FaTimesCircle /> Absent
                                        </span>
                                      );
                                    }
                                    if (statLower === 'late') {
                                      return (
                                        <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1">
                                          <FaClock /> Late
                                        </span>
                                      );
                                    }
                                    if (statLower === 'half day' || statLower === 'halfday' || statLower === 'half_day') {
                                      return (
                                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1">
                                          Half Day
                                        </span>
                                      );
                                    }
                                    if (statLower === 'leave') {
                                      return (
                                        <span className="badge bg-opacity-10 rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1" style={{ color: "#7c3aed", borderColor: "#ddd6fe", backgroundColor: "#f5f3ff", border: "1px solid" }}>
                                          Leave
                                        </span>
                                      );
                                    }
                                    return (
                                      <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1">
                                        Not Marked
                                      </span>
                                    );
                                  })()}
                                </td>
                                <td style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7" }}>
                                  <span className={`fw-semibold ${!student.clock_in ? 'text-muted' : 'text-primary'}`}>
                                    {student.clock_in || '-'}
                                  </span>
                                </td>
                                <td style={{ border: "1px solid #74c69d", backgroundColor: "#f2fbf7" }}>
                                  <span className={`fw-semibold ${!student.clock_out ? 'text-muted' : 'text-danger'}`}>
                                    {student.clock_out || '-'}
                                  </span>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={isDateRange ? matrixDates.length + 9 : 7} className="text-center text-muted p-4">No students found</td>
                      </tr>
                    )}
                  </tbody>
                  {currentStudents.length > 0 && (
                    <tfoot className="fw-bold text-center align-middle" style={{ backgroundColor: "#b7e4c7", color: "#1b4332", borderTop: "2px solid #74c69d" }}>
                      <tr>
                        <td colSpan={isDateRange ? 3 : 3} className="text-end text-uppercase pe-3" style={{ letterSpacing: "1px", color: "#1b4332", border: "1px solid #74c69d", backgroundColor: "#b7e4c7" }}>
                          TOTAL :
                        </td>
                        {isDateRange ? (
                          <>
                            {matrixDates.map((_, idx) => (
                              <td key={idx} className="text-center fw-bold" style={{ fontSize: "11px", padding: "6px 2px", color: "#1b4332", border: "1px solid #74c69d", backgroundColor: "#b7e4c7" }}>
                                {getDateColumnTotals(idx)}
                              </td>
                            ))}
                            <td style={{ backgroundColor: "#d8f3dc", color: "#1b4332", border: "1px solid #74c69d" }}>{overallSummaryTotals.p}</td>
                            <td style={{ backgroundColor: "#ffccd5", color: "#800f2f", border: "1px solid #74c69d" }}>{overallSummaryTotals.a}</td>
                            <td style={{ backgroundColor: "#fff3b0", color: "#9a031e", border: "1px solid #74c69d" }}>{overallSummaryTotals.l}</td>
                            <td style={{ backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #74c69d" }}>{overallSummaryTotals.hd}</td>
                            <td style={{ backgroundColor: "#f3e8ff", color: "#6b21a8", border: "1px solid #74c69d" }}>{overallSummaryTotals.lv}</td>
                            <td style={{ border: "1px solid #74c69d", backgroundColor: "#b7e4c7" }}></td>
                          </>
                        ) : (
                          <td colSpan="4" className="text-start" style={{ color: "#1b4332", border: "1px solid #74c69d", backgroundColor: "#b7e4c7" }}>
                            Matching Students: {filteredStudents.length}
                          </td>
                        )}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <span className="text-muted" style={{ fontSize: '14px' }}>
                    Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredStudents.length)} of {filteredStudents.length} entries
                  </span>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link shadow-none rounded-start-pill" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                        Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                      <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                        <button className="page-link shadow-none" onClick={() => setCurrentPage(i + 1)}>
                          {i + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link shadow-none rounded-end-pill" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                        Next
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleClassAttendanceLayer;
